import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faPlus,
  faClock,
  faDollarSign,
  faBan,
  faMoneyBillAlt,
  faExclamationCircle,
  faQuestionCircle,
  faRedo,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../../api/api";
import {
  CURRENCY_CODE,
  WALK_STATUS,
} from "../../config/constants";
import "react-phone-input-2/lib/style.css";
import WalkDetailModal from "./partials/WalkDetails";
import WalkRequest from "./partials/WalkRequest";
import CancelWalk from "./partials/CancelWalk";
import Pagination from "../../components/Pagination";
import { formatDistanceToNow } from "date-fns";
import { capitalizeWords, routes } from "../../config/utility";
import LoadingLogo from "../../components/Loading";
import WalkPaymentMethod from "./partials/WalkPaymentMethod";
import { Link } from "react-router-dom";

const PetOwnerWalk = ({ user, userRoles, userPermission, updateContext }) => {
  const [walks, setWalks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShowModalOpen, setIsShowModalOpen] = useState(false);
  const [isCancelWalkModalOpen, setIsCancelWalkModalOpen] = useState(false);
  const [isWalkPaymentMethodModalOpen, setIsWalkPaymentMethodModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [ownerPets, setOwnerPets] = useState([]);
  const [tools, setTools] = useState([]);
  const [limit, setLimit] = useState(0);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [meta, setMeta] = useState({
    total: 0,
    per_page: 6,
    current_page: 1,
    last_page: 1,
    from: 1,
    to: 1,
  });
  const [editWalk, setEditWalk] = useState(null); // For editing existing walk

  const statusColorMap = {
    requested: "bg-purple-200 text-purple-800",
    finding_walker: "bg-purple-200 text-purple-800",
    completed: "bg-green-200 text-green-800",
    cancelled: "bg-red-200 text-red-800",
    walker_assigned: "bg-purple-800 text-purple-200",
    heading_to_pickup: "bg-purple-800 text-purple-200",
    picked_up: "bg-purple-800 text-purple-200",
    in_progress: "bg-purple-800 text-purple-200",
  };

  // Fetch walks on mount and when filters/pagination change
  useEffect(() => {
    fetchWalks();
  }, [statusFilter, currentPage]);

  const fetchWalks = async () => {
    setLoading(true);
    try {
      const response = await api.get("/walks", {
        params: {
          search: searchTerm,
          status: statusFilter,
          page: currentPage,
          per_page: meta.per_page,
        },
      });
      setWalks(response.data);
      setMeta(response.meta);
      setOwnerPets(response.pets);
      setTools(response.tools);
      setLimit(response.limit);

      setLoading(false);
    } catch (error) {
      toast.error("Failed to fetch walk history.");
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditWalk(null); // New walk
    setIsModalOpen(true);
  };

  const handleReorder = (walk) => {
    // Check limit before allowing reorder
    if (limit === 3) {
      toast.error(
        "You have reached the max limit for 3 walks at a time. Either complete or cancel a walk to request another."
      );
      return;
    }

    // Create a reorder object with the walk data formatted for the form
    const reorderData = {
      pickup_latitude: walk.pickup_location?.latitude || null,
      pickup_longitude: walk.pickup_location?.longitude || null,
      pickup_address: walk.pickup_location?.address || "",
      pet_ids: walk.pets?.map(pet => pet.id) || [],
      clean_pet: walk.clean_pet || false,
      scheduled_at: false, // Always set to instant for reorder
      scheduled_time: null,
      emergency_no: walk.emergency_no || "",
      promo_code: "", // Reset promo code for reorder
      duration_walk: walk.duration || "",
      tool_ids: walk.tools?.map(tool => tool.id) || [],
      services: ["walk"],
      day: "",
      walkSlot: null,
      slot_token: null,
      scheduleType: "instant",
      termsAccepted: false,
      // Add reorder flag to indicate this is a reorder
      isReorder: true,
      originalWalkId: walk.id
    };

    setEditWalk(reorderData);
    setIsModalOpen(true);
    // toast.success("Walk data loaded for reorder!");
  };

  // const handleEdit = (walk) => {
  //   setEditWalk(walk); // Edit walk
  //   setIsModalOpen(true);
  // };

  const handleShow = (walk) => {
    setEditWalk(walk); // walk
    setIsShowModalOpen(true);
  };

  const handleCancelWalk = (walk) => {
    setEditWalk(walk); // walk
    setIsCancelWalkModalOpen(true);
  };

  const handleWalkPaymentMethod = (walk) => {
    setEditWalk(walk); // walk
    setIsWalkPaymentMethodModalOpen(true);
  };

  const handleSave = () => {
    setIsModalOpen(false);
    fetchWalks();
  };

  const handleCancel = () => {
    setIsCancelWalkModalOpen(false);
    fetchWalks();
  };

  const handleCancelWalkPaymentMethod = () => {
    setIsWalkPaymentMethodModalOpen(false);
    fetchWalks();
  };

  const handleNextPage = () => {
    if (meta.current_page < meta.last_page) {
      setCurrentPage(meta.current_page + 1);
    }
  };

  const handlePrevPage = () => {
    if (meta.current_page > 1) {
      setCurrentPage(meta.current_page - 1);
    }
  };

  const handleShowModalClose = (shouldFetchWalks) => {
    setIsShowModalOpen(false);
    if (shouldFetchWalks) {
      fetchWalks();
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= meta.last_page; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 rounded-full ${i === meta.current_page
            ? "bg-primary text-white"
            : "bg-white text-gray-700"
            } hover:bg-primary hover:text-white mx-1`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  if (loading) {
    return <LoadingLogo />;
  }

  return (
    <div className="mx-auto  ">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-primary text-2xl sm:text-3xl font-bold">Paw Walk</h1>
          <Link
            to={`${routes.guide}?type=walk`}
            className="group flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-100 hover:bg-purple-200 transition-all duration-300"
            title="View Walk Guide"
          >
            <FontAwesomeIcon
              icon={faQuestionCircle}
              className="text-primary text-lg sm:text-xl group-hover:scale-110 transition-transform duration-300"
            />
          </Link>
        </div>
        <button
          onClick={() => {
            if (limit === 3) {
              toast.warning(
                "You have reached the max limit for 3 walks at a time. Either complete or cancel a walk to request another.",
                { autoClose: 5000 }
              );
              return;
            }
            handleAdd();
          }}
          disabled={limit === 3}
          className={`w-full sm:w-auto bg-primary text-white px-4 py-2 rounded-lg flex items-center justify-center transition-all duration-300
    ${limit === 3 ? "opacity-60 cursor-not-allowed" : "hover:bg-accent"}`}
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Request New Walk
        </button>
      </div>

      {/* Filters Section */}
      <div className="flex flex-col space-y-4 mb-6">
        {/* More Filters Button */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="w-full sm:w-auto text-purple-700 font-semibold hover:text-purple-900 transition-all duration-300 py-2 px-4 rounded-lg hover:bg-purple-50"
          >
            {showAdvancedFilters ? "🔼 Hide Filters" : "🔽 More Filters"}
          </button>

          {/* Primary Walk Filters */}
          <div className="flex flex-wrap gap-2 justify-center w-full sm:w-auto">
            {["all", "ongoing", "scheduled"].map((filter) => (
              <label
                key={filter}
                className={`flex-1 sm:flex-initial flex items-center justify-center space-x-2 cursor-pointer px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-all duration-300 shadow-md 
            ${statusFilter === filter
                    ? "bg-white text-tblue shadow-lg scale-105"
                    : "bg-tblue text-white hover:scale-105"}`}
              >
                <input
                  type="radio"
                  name="walkStatus"
                  value={filter}
                  checked={statusFilter === filter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="hidden"
                />
                <span className="text-base sm:text-lg">
                  {filter === "all" ? "🐾" : filter === "ongoing" ? "🚶‍♂️" : "📅"}
                </span>
                <span className="text-xs sm:text-sm font-semibold capitalize">{filter}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Advanced Filters */}
        <div className={`transition-all duration-500 overflow-hidden ${showAdvancedFilters ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
          <div className="p-2 sm:p-5">
            <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
              {Object.entries(WALK_STATUS).map(([key, value]) => (
                <label
                  key={key}
                  className={`flex-1 sm:flex-initial min-w-[150px] flex items-center justify-center space-x-2 cursor-pointer px-3 sm:px-6 py-2 sm:py-3 rounded-full transition-all duration-300 shadow-md 
              ${statusFilter === value
                      ? "bg-white text-tblue shadow-lg scale-105"
                      : "bg-tblue text-white hover:scale-105"}`}
                >
                  <input
                    type="radio"
                    name="walkStatus"
                    value={value}
                    checked={statusFilter === value}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="hidden"
                  />
                  <span className="text-base sm:text-lg">🐶</span>
                  <span className="text-xs sm:text-sm font-medium">{capitalizeWords(value)}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Walks Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {walks.length > 0 ? (
          walks.map((walk) => (
            <div
              key={walk.id}
              className="relative rounded-xl p-4 sm:p-6 shadow-lg transition-transform transform duration-300 hover:scale-102 hover:shadow-xl bg-gradient-to-br from-purple-200 via-bgBody to-pink-100"
            >
              {/* Status Badge */}
              <span
                className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold ${statusColorMap[walk.status]}`}
              >

                {(walk.status === WALK_STATUS.REQUESTED || walk.status === WALK_STATUS.FINDING_WALKER) && walk.scheduled_at ? (
                  <> Scheduled to start {formatDistanceToNow(new Date(walk.scheduled_at), { addSuffix: true })} </>
                ) : capitalizeWords(walk.status)}
              </span>

              {walk.payment_status == 1 && <>
                <span
                  className={` absolute bottom-4 left-4 px-3 py-1 rounded-full text-xs font-bold bg-green-200 text-green-800`}
                >
                  Paid
                </span>
              </>}


              {/* Stacked Pet Images */}
              {walk.pets && walk.pets.length > 0 && (
                <div className="flex -space-x-3 mb-4">
                  {walk.pets.slice(0, 3).map((pet, index) => (
                    <img
                      key={index}
                      src={pet.primary_image_url}
                      alt={pet.name}
                      className="w-12 h-12 rounded-full object-cover shadow-lg border-2 border-white"
                      style={{ zIndex: walk.pets.length - index }}
                    />
                  ))}
                  {walk.pets.length > 3 && (
                    <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-sm font-bold text-white border-2 border-white shadow-lg">
                      +{walk.pets.length - 3}
                    </div>
                  )}
                </div>
              )}

              {/* Walker and Charge Info */}
              <div className="space-y-4 mt-4">
                {/* <div className="flex items-center space-x-2 text-gray-800">
                  <FontAwesomeIcon
                    icon={faDollarSign}
                    className="text-primary"
                  />
                  <span className="font-semibold">
                    <span className="text-tblue">Charge:</span>{" "}
                    {walk.charges.total} {CURRENCY_CODE}
                  </span>
                </div> */}

                {/* <div className="flex items-center space-x-2 text-gray-800">
                  <FontAwesomeIcon
                    icon={faLocationPinLock}
                    className="text-primary"
                  />
                  <span className="font-semibold">
                    <span className="text-tblue">Pickup:</span>{" "}
                    {walk.pickup_location ? walk.pickup_location.address : ""}
                  </span>
                </div> */}

                <div className="flex items-center space-x-2 text-gray-800">
                  <FontAwesomeIcon
                    icon={faMoneyBillAlt}
                    className="text-primary"
                  />
                  <span className="font-semibold">
                    <span className="text-tblue">Payment Method:</span>{" "}
                    {walk.payment_method ? (
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-white text-primary">
                        {walk.payment_method}
                      </span>
                    ) : (
                      walk.status === WALK_STATUS.CANCELLED ? (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-200 text-red-600">
                          Not Selected
                        </span>
                      ) : (
                        <button
                          onClick={() => handleWalkPaymentMethod(walk)}
                          className="text-white text-xs p-2 px-3 rounded-lg bg-red-600 shadow-md transition-all transform hover:scale-105 hover:shadow-lg active:scale-100"
                          title="Select Payment Method"
                        >
                          <FontAwesomeIcon icon={faExclamationCircle} className="text-yellow-300" />
                          <span className="ms-2 font-semibold">Select Payment Method</span>
                        </button>
                      )
                    )}


                  </span>
                </div>

                <div className="flex items-center space-x-2 text-gray-800">
                  <div className="flex items-center space-x-2 text-gray-500 mt-2">
                    <FontAwesomeIcon icon={faClock} className="text-primary" />
                    <span className="font-semibold  text-tblue">
                      {formatDistanceToNow(new Date(walk.created_at), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => handleShow(walk)}
                  className="text-blue-500 hover:text-blue-700 transition-transform transform hover:scale-110"
                  title="View Walk Details"
                >
                  <FontAwesomeIcon icon={faEye} />
                </button>

                {/* Reorder Button - Show for completed or cancelled walks */}
                {(walk.status === WALK_STATUS.COMPLETED || walk.status === WALK_STATUS.CANCELLED || walk.status == WALK_STATUS.REQUESTED ||
                  walk.status == WALK_STATUS.FINDING_WALKER || walk.status == WALK_STATUS.WALKER_ASSIGNED || walk.status == WALK_STATUS.HEADING_TO_PICKUP ||
                  walk.status == WALK_STATUS.PICKED_UP || walk.status == WALK_STATUS.IN_PROGRESS) && (
                    <button
                      onClick={() => handleReorder(walk)}
                      className={`transition-transform transform ${limit === 3
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-green-500 hover:text-green-700 hover:scale-110"
                        }`}
                      title={limit === 3 ? "Walk limit reached (3/3)" : "Reorder Walk"}
                      disabled={limit === 3}
                    >
                      <FontAwesomeIcon icon={faRedo} />
                    </button>
                  )}

                {(walk.status == WALK_STATUS.REQUESTED ||
                  walk.status == WALK_STATUS.FINDING_WALKER) && (
                    <button
                      onClick={() => handleCancelWalk(walk)}
                      className="text-red-500 hover:text-red-700 transition-transform transform hover:scale-110"
                      title="Cancel Walk"
                    >
                      <FontAwesomeIcon icon={faBan} />
                    </button>
                  )}


              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center text-gray-500 py-8">
            No walks found.
          </div>
        )}
      </div>

      {/* Smart Pagination */}
      <div className="mt-6">
        <Pagination
          meta={meta}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      {/* Modals remain unchanged */}
      {isModalOpen && (
        <WalkRequest
          item={editWalk}
          pets={ownerPets}
          tools={tools}
          onSave={handleSave}
          permission={userPermission}
          onCancel={() => setIsModalOpen(false)}
          userUpdateContext={updateContext}
        />
      )}

      {isShowModalOpen && (
        <WalkDetailModal
          walk={editWalk}
          isOpen={isShowModalOpen}
          onClose={handleShowModalClose}
        />
      )}

      {isCancelWalkModalOpen && (
        <CancelWalk
          onCancel={handleCancel}
          isOpen={isCancelWalkModalOpen}
          walk={editWalk}
          userUpdateContext={updateContext}
        />
      )}

      {isWalkPaymentMethodModalOpen && (
        <WalkPaymentMethod
          onCancel={handleCancelWalkPaymentMethod}
          isOpen={isWalkPaymentMethodModalOpen}
          walk={editWalk}
        />
      )}
    </div>
  );
};

export default PetOwnerWalk;
